import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { celebrate } from '../../stores/moment.store';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import type { DealItem, DealListResponse } from './dealsApi';

/**
 * Azioni condivise sulle trattative (lista, kanban, dettaglio).
 *
 * Stesso pattern di useTaskActions: ogni mutation invalida ['deals'] (e
 * ['tasks'], perché la prossima azione è un task vero e cambia la lista Task),
 * gestisce toast/undo; i consumer chiamano solo `.mutate(...)` e usano
 * `.isPending` per il disable.
 *
 * Gli step sono dati, non enum: qui girano sempre `stageId` e il nome serve
 * solo per i messaggi.
 */

export interface QuickCreateArgs {
  title: string;
  companyName?: string | null;
  pipeline?: string;
  stageId?: string;
  /** Voucher: la configurazione (il ricavo lo calcola il server). */
  scenario?: string | null;
  /** Solo fuori dal voucher. */
  amount?: number;
  expectedCloseDate?: string | null;
}

export interface KanbanMoveArgs {
  id: string;
  stageId: string;
  stageName: string;
  outcome: 'APERTO' | 'VINTO' | 'PERSO';
  position: number;
  order: number;
  prevStageId: string;
  title: string;
  lostReason?: string | null;
  /** Query della kanban da aggiornare in modo ottimistico. */
  queryKey: readonly unknown[];
}

export function useDealActions() {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['deals'] });
    // La prossima azione è un Task: lista, riepilogo e badge sidebar cambiano
    qc.invalidateQueries({ queryKey: ['tasks'] });
  };

  /** Crea: quick-add della lista e modale "Nuova trattativa". */
  const create = useMutation({
    mutationFn: (args: QuickCreateArgs & Record<string, unknown>) => api.post<DealItem>('/deals', args),
    onSuccess: (_r, vars) => { invalidate(); toast.ok('Trattativa creata', vars.title); },
    onError: (e: any) => toast.alert('Trattativa non creata', e?.message ?? 'Riprova'),
  });

  /** PATCH parziale (campi del dettaglio, salvataggio al cambio). */
  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => api.patch<DealItem>(`/deals/${id}`, data),
    onSuccess: invalidate,
    onError: (e: any) => toast.alert('Modifica non salvata', e?.message ?? 'Riprova'),
  });

  /** Vinta: la porta a casa. Celebrazione e Annulla (torna allo step di prima). */
  const win = useMutation({
    mutationFn: ({ id, stageId }: { id: string; title?: string; stageId: string; stageName?: string; prevStageId: string }) =>
      api.patch<DealItem>(`/deals/${id}`, { stageId }),
    onSuccess: (_r, vars) => {
      invalidate();
      celebrate({ title: vars.stageName ?? 'Trattativa vinta', subtitle: vars.title });
      toast.undo(`Segnata come ${(vars.stageName ?? 'vinta').toLowerCase()}`, {
        description: vars.title,
        onUndo: () => api.patch(`/deals/${vars.id}`, { stageId: vars.prevStageId }).then(invalidate)
          .catch((e: any) => toast.alert('Ripristino non riuscito', e?.message ?? 'Riprova')),
      });
    },
    onError: (e: any) => toast.alert('Non salvata come vinta', e?.message ?? 'Riprova'),
  });

  /** Chiudi: uno dei modi in cui una trattativa si ferma, col motivo. */
  const close = useMutation({
    mutationFn: ({ id, stageId, lostReason }: { id: string; title?: string; prevStageId: string; stageId: string; stageName: string; lostReason: string }) =>
      api.patch<DealItem>(`/deals/${id}`, { stageId, lostReason }),
    onSuccess: (r, vars) => {
      invalidate();
      toast.undo(`Chiusa: ${vars.stageName.toLowerCase()}`, {
        description: vars.title ?? r.title,
        onUndo: () => api.patch(`/deals/${vars.id}`, { stageId: vars.prevStageId }).then(invalidate)
          .catch((e: any) => toast.alert('Ripristino non riuscito', e?.message ?? 'Riprova')),
      });
    },
    onError: (e: any) => toast.alert('Trattativa non chiusa', e?.message ?? 'Riprova'),
  });

  /** Sposta in un'altra pipeline: serve lo step di arrivo, i nomi non si somigliano. */
  const movePipeline = useMutation({
    mutationFn: ({ id, pipeline, stageId }: { id: string; title?: string; pipeline: string; pipelineName: string; stageId: string }) =>
      api.patch<DealItem>(`/deals/${id}`, { pipeline, stageId }),
    onSuccess: (_r, vars) => { invalidate(); toast.ok(`Spostata in ${vars.pipelineName}`, vars.title); },
    onError: (e: any) => toast.alert('Spostamento non riuscito', e?.message ?? 'Riprova'),
  });

  /**
   * Drag nella kanban: POST /deals/:id/move { stageId, position, order }.
   * Ottimistico (la card cambia colonna subito, rollback su errore).
   */
  const kanbanMove = useMutation({
    mutationFn: ({ id, stageId, position, order, lostReason }: KanbanMoveArgs) =>
      api.post<DealItem>(`/deals/${id}/move`, { stageId, position, order, lostReason: lostReason ?? undefined }),
    onMutate: (vars) => {
      const prev = qc.getQueryData<DealListResponse>(vars.queryKey);
      if (prev) {
        const moving = prev.items.find((d) => d.id === vars.id);
        if (moving) {
          const others = prev.items.filter((d) => d.id !== vars.id);
          qc.setQueryData<DealListResponse>(vars.queryKey, {
            ...prev,
            items: [...others, {
              ...moving,
              stageId: vars.stageId,
              stage: { ...moving.stage, id: vars.stageId, name: vars.stageName, outcome: vars.outcome },
              isOpen: vars.outcome === 'APERTO',
              isWon: vars.outcome === 'VINTO',
              isClosed: vars.outcome === 'PERSO',
              order: vars.order,
            }],
          });
        }
      }
      void qc.cancelQueries({ queryKey: vars.queryKey });
      return { prev };
    },
    onError: (e: any, vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(vars.queryKey, ctx.prev);
      toast.alert('Spostamento non riuscito', e?.message ?? 'Riprova');
    },
    onSuccess: (_r, vars) => {
      if (vars.stageId === vars.prevStageId) return;
      if (vars.outcome === 'VINTO') celebrate({ title: vars.stageName, subtitle: vars.title });
    },
    onSettled: invalidate,
  });

  /** Prossima azione: crea il Task collegato (compare anche in /tasks). */
  const nextAction = useMutation({
    mutationFn: ({ id, ...body }: { id: string; title: string; dueDate?: string | null; assigneeId?: string | null }) =>
      api.post<{ id: string; title: string }>(`/deals/${id}/next-action`, body),
    onSuccess: (r) => { invalidate(); toast.ok('Azione programmata', `${r.title} · la trovi anche in Task`); },
    onError: (e: any) => toast.alert('Azione non programmata', e?.message ?? 'Riprova'),
  });

  /**
   * Rinnovo vinto: prolunga il canone che c'è già invece di aprire una
   * commessa nuova. Invalida anche i ricorrenti e l'Amministrazione, perché
   * quella riga vive lì.
   */
  const renew = useMutation({
    mutationFn: ({ id, ...body }: { id: string; contratto?: string; endDate: string; amountNet?: number; startDate?: string }) =>
      api.post<{ cambiaPrezzo: boolean; contratto: { name: string; amountNet: number; endDate: string | null } }>(`/deals/${id}/renew`, body),
    onSuccess: (r, vars) => {
      invalidate();
      qc.invalidateQueries({ queryKey: ['admin'] });
      qc.invalidateQueries({ queryKey: ['client'] });
      toast.ok(
        r.cambiaPrezzo ? 'Canone rinnovato al nuovo prezzo' : 'Canone prolungato',
        `${vars.contratto ?? r.contratto.name} · fino al ${r.contratto.endDate ?? '—'}`,
      );
    },
    onError: (e: any) => toast.alert('Rinnovo non registrato', e?.message ?? 'Riprova'),
  });

  /** Nota o contatto nello storico. */
  const addEvent = useMutation({
    mutationFn: ({ id, ...body }: { id: string; kind: 'NOTA' | 'CONTATTO'; text: string }) => api.post(`/deals/${id}/events`, body),
    onSuccess: invalidate,
    onError: (e: any) => toast.alert('Nota non salvata', e?.message ?? 'Riprova'),
  });

  /** Elimina (soft) con conferma. Ritorna null se l'utente annulla. */
  const remove = useMutation({
    mutationFn: async ({ id, title }: { id: string; title?: string }) => {
      const ok = await confirmDialog({
        title: 'Eliminare questa trattativa?',
        body: title ? `"${title}" sparisce dalla pipeline.` : undefined,
        danger: true,
        confirmLabel: 'Elimina',
        icon: 'trash',
      });
      if (!ok) return null;
      await api.delete(`/deals/${id}`);
      return true;
    },
    onSuccess: (done, vars) => {
      if (!done) return;
      // Il dettaglio appena eliminato risponderebbe 404: si rifetcha il resto
      qc.invalidateQueries({ queryKey: ['deals'], predicate: (q) => q.queryKey[1] !== 'detail' });
      toast.ok('Trattativa eliminata', vars.title);
    },
    onError: (e: any) => toast.alert('Eliminazione non riuscita', e?.message ?? 'Riprova'),
  });

  return { create, update, win, close, movePipeline, kanbanMove, nextAction, addEvent, renew, remove };
}
